import { useCallback, useEffect, useState } from "react";
import FormGroup from "./FormGroup";

export type TValidator<T> = (value:T) => boolean|string;
export type TValidators<T> = TValidator<T>[]
export type TErrorer<T> = (value:T, validator:TValidator<T>) => string;

export interface IValidator {
    valid:boolean;
    dirty:boolean;
    touched:boolean;
}

export default class Validators {
    static Required(value:any) { return !!value }
    static PositiveNumber(value:any) { return Validators.Number(value) && value>0 }
    static GreaterOrEqualZero(value:any) { return Validators.Number(value) && value>=0 }
    static NonZero(value:any) { return Validators.Number(value) && (value>0||value<0) }
    static Number(value:any) : value is number {
        return typeof(value)=="number" || !isNaN(parseFloat(value))
    }
    static errorer<T>(name:string, value:any, validator:TValidator<T>){
        switch(validator){
        case Validators.Required:       return `${name} is required`
        case Validators.PositiveNumber: return `${name} must be a positive number`
        case Validators.NonZero:        return `${name} cannot be zero`
        case Validators.Number:         return `${name} must be a number`
        default:                        return `${value} is not valid ${name}`
        }
    }
}

export interface IFormFieldProps<T> {
    value?:T;
    controlName?:string;
    hint?:string;
    placeholder?:string;
    floatLabel?:string;
    form?:FormGroup;
    errorer?:TErrorer<T>;
}


export function useValidator<T>(props:IFormFieldProps<T>){
    const form = props.form
    const control = props.controlName ? form?.control(props.controlName) : null

    const [value,_setValue] = useState<T|undefined>(props.value)
    const [hasError,setHasError] = useState<boolean>((control?.dirty && control?.invalid) || false)

    const onRefresh = () => setHasError(control?.invalid || false)
    useEffect(() => {
        control?.on("refresh", onRefresh)
        return () => {control?.removeListener("refresh", onRefresh)}
    },[])

    useEffect(() => {
        control?.update(props.value)
    }, [props.value])
    
    let hint:string = ""
    if (hasError){
        if (props.hint) hint = props.hint
        else if (control) hint = control.errorer(value, props.errorer)
        else hint = "Invalid"
    }

    const setValue = useCallback((val:any) => {
        _setValue(val)
        control?.update(val)
    }, [])
    return {value, setValue, hint, form, control, hasError}
}